import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import SettingsHubPanel from '../../components/SettingsHubPanel';
import TextInput from '../../components/ui/TextInput';
import { Button } from '../../components/ui/Button';
import { Label } from '../../components/ui/Label';
import { Switch } from '../../components/ui/Switch';
import { AccordionItem } from '../../components/ui/Accordion';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import {
  useSystemEmailTemplates,
  updateSystemEmailTemplate,
  type SystemEmailTemplateListItem,
} from './useData';
import { cn } from '../../lib/utils';
import Card from '../../components/ui/Card';
import type { EmbeddedInConfigurationHub } from '../configuration-settings/models';

const SYSTEM_EMAIL_TEMPLATE_EXAMPLES: Record<string, { subject: string; bodyHtml: string }> = {
  user_invitation: {
    subject: "You're invited to join",
    bodyHtml:
      '<p>You have been invited to create an account.</p>\n' +
      '<p>Click the link below to complete your registration. This link expires in {{expiryDays}} days.</p>\n' +
      '<p><a href="{{registerLink}}">{{registerLink}}</a></p>\n' +
      '<p>Best regards,<br />{{appName}}</p>',
  },
  password_reset: {
    subject: 'Reset your password',
    bodyHtml:
      '<p>Hello {{userName}},</p>\n' +
      '<p>You requested a password reset. Click the link below to set a new password:</p>\n' +
      '<p><a href="{{resetLink}}">{{resetLink}}</a></p>\n' +
      '<p>Best regards,<br />{{appName}}</p>',
  },
};

export type SystemEmailTemplatesPageProps = EmbeddedInConfigurationHub;

const SystemEmailTemplatesPage: React.FC<SystemEmailTemplatesPageProps> = ({ embedded = false }) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const isAdmin = user?.role === 'ADMIN';

  const {
    templates: systemEmailTemplates,
    globalPlaceholders: systemEmailGlobalPlaceholders,
    isLoading: systemTemplatesLoading,
    mutate: mutateSystemTemplates,
  } = useSystemEmailTemplates();

  const [editingTemplate, setEditingTemplate] = useState<SystemEmailTemplateListItem | null>(null);
  const [templateForm, setTemplateForm] = useState({ subject: '', bodyHtml: '', isEnabled: true });
  const [savingTemplate, setSavingTemplate] = useState(false);

  const handleSaveSystemEmailTemplate = async () => {
    if (!editingTemplate) return;
    setSavingTemplate(true);
    try {
      await updateSystemEmailTemplate(editingTemplate.key, {
        subject: templateForm.subject,
        bodyHtml: templateForm.bodyHtml || undefined,
        isEnabled: templateForm.isEnabled,
      });
      await mutateSystemTemplates();
      setEditingTemplate(null);
      showSuccess('Template saved');
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  if (!isAdmin) {
    if (embedded) {
      return (
        <Card variant="outlined" className="p-6 text-center">
          <p className={cn('text-sm', colors.text)}>System email templates are only available to administrators.</p>
        </Card>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h2 className={cn('mb-2 text-2xl font-bold', colors.text)}>Access denied</h2>
          <p className={cn('text-sm', colors.textMuted)}>This page is only available to administrators.</p>
        </div>
      </div>
    );
  }

  if (systemTemplatesLoading) {
    if (embedded) {
      return (
        <div className="py-10">
          <LoadingSpinner message="Loading templates…" />
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner message="Loading templates…" size="large" />
      </div>
    );
  }

  const templateBody = (
    <>
      <div className={cn('rounded-lg border p-3', colors.border, colors.surface)}>
        <p className={cn('text-xs font-medium mb-1.5', colors.text)}>Global (all templates)</p>
        <ul className={cn('text-xs space-y-0.5', colors.textMuted)}>
          {systemEmailGlobalPlaceholders.map((g) => (
            <li key={g.placeholder}>
              <code className="bg-black/5 dark:bg-white/10 px-1 rounded">{g.placeholder}</code> — {g.label}
            </li>
          ))}
        </ul>
      </div>
      <div className="space-y-3">
        {systemEmailTemplates.map((t) => (
          <div
            key={t.key}
            className={cn('flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border', colors.border, colors.surface)}
          >
            <div className="min-w-0">
              <p className={cn('font-medium text-sm', colors.text)}>
                {t.key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </p>
              <p className={cn('text-xs mt-0.5', colors.textMuted)}>{t.description}</p>
              <p className={cn('text-xs mt-1', colors.textMuted)}>
                This template: {t.placeholders.length ? t.placeholders.join(', ') : '—'}
              </p>
              <span
                className={cn(
                  'mt-1 inline-block rounded px-1.5 py-0.5 text-xs font-medium',
                  (t.isEnabled ?? true)
                    ? 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
                    : cn('bg-black/[0.04] dark:bg-white/[0.06]', colors.textMuted),
                )}
              >
                {(t.isEnabled ?? true) ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setEditingTemplate(t);
                setTemplateForm({
                  subject: t.subject,
                  bodyHtml: t.bodyHtml ?? '',
                  isEnabled: t.isEnabled ?? true,
                });
              }}
            >
              Edit
            </Button>
          </div>
        ))}
      </div>
    </>
  );

  const editModal = (
      <Modal isOpen={!!editingTemplate} onClose={() => setEditingTemplate(null)} size="lg">
        {editingTemplate && (
          <>
            <ModalHeader>
              <ModalTitle>
                Edit: {editingTemplate.key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </ModalTitle>
            </ModalHeader>
            <ModalContent>
              <p className={cn('text-sm mb-3', colors.textMuted)}>{editingTemplate.description}</p>
              <div className="flex items-center gap-2 mb-3">
                <Switch
                  id="template-enabled"
                  checked={templateForm.isEnabled}
                  onChange={(e) => setTemplateForm((f) => ({ ...f, isEnabled: e.target.checked }))}
                />
                <Label htmlFor="template-enabled" className={cn('text-sm', colors.text)}>
                  Enabled
                </Label>
              </div>
              <div className={cn('mb-3 p-3 rounded border', colors.border, colors.surface)}>
                <p className={cn('text-xs font-medium mb-1.5', colors.text)}>Global (all templates)</p>
                <ul className={cn('text-xs', colors.textMuted)}>
                  {systemEmailGlobalPlaceholders.map((g) => (
                    <li key={g.placeholder}>
                      <code className="bg-black/5 dark:bg-white/10 px-1 rounded">{g.placeholder}</code> — {g.label}
                    </li>
                  ))}
                </ul>
              </div>
              <p className={cn('text-xs mb-2', colors.textMuted)}>
                This template: {editingTemplate.placeholders.length ? editingTemplate.placeholders.join(', ') : '—'}
              </p>
              {SYSTEM_EMAIL_TEMPLATE_EXAMPLES[editingTemplate.key] && (
                <AccordionItem title="Example (with placeholders)" defaultOpen={false} className="mb-4">
                  <div className={cn('p-3 rounded border bg-black/5 dark:bg-white/5', colors.border)}>
                    <p className={cn('text-xs mb-1', colors.text)}>
                      <span className="font-semibold">Subject:</span> {SYSTEM_EMAIL_TEMPLATE_EXAMPLES[editingTemplate.key].subject}
                    </p>
                    <p className={cn('text-xs mb-1', colors.text)}>Body HTML:</p>
                    <pre
                      className={cn(
                        'text-xs whitespace-pre-wrap break-words rounded px-2 py-1 font-mono',
                        'bg-black/10 dark:bg-white/10',
                        colors.text
                      )}
                    >
                      {SYSTEM_EMAIL_TEMPLATE_EXAMPLES[editingTemplate.key].bodyHtml}
                    </pre>
                  </div>
                </AccordionItem>
              )}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="template-subject">Subject</Label>
                  <TextInput
                    id="template-subject"
                    value={templateForm.subject}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, subject: e.target.value }))}
                    placeholder="Email subject"
                    className="mt-1 w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="template-body">HTML body</Label>
                  <Textarea
                    id="template-body"
                    value={templateForm.bodyHtml}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, bodyHtml: e.target.value }))}
                    placeholder="<p>HTML content. Use placeholders above.</p>"
                    rows={12}
                    className={cn('mt-1 w-full font-mono text-sm', colors.background, colors.text, colors.border)}
                  />
                </div>
              </div>
            </ModalContent>
            <ModalFooter>
              <Button type="button" variant="outline" onClick={() => setEditingTemplate(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={() => void handleSaveSystemEmailTemplate()}
                loading={savingTemplate}
                disabled={savingTemplate}
              >
                Save
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
  );

  if (embedded) {
    return (
      <>
        <SettingsHubPanel
          title="System email templates"
          description="Customize subject and HTML for transactional emails. Use placeholders as documented below."
          helpItems={[
            {
              summary: 'SMTP delivery',
              content: (
                <p>
                  Outbound mail uses the server configured under{' '}
                  <strong className={cn('font-medium', colors.text)}>Settings → Email notifications</strong>.
                </p>
              ),
            },
          ]}
          contentClassName="space-y-4"
        >
          {templateBody}
        </SettingsHubPanel>
        {editModal}
      </>
    );
  }

  return (
    <>
      <div className="h-full overflow-y-auto">
        <div className="mx-auto max-w-4xl space-y-6 p-6">
          <div>
            <h1 className={cn('text-2xl font-semibold tracking-tight', colors.text)}>System email templates</h1>
            <p className={cn('mt-1 text-sm', colors.textMuted)}>
              Subjects and HTML bodies for user invitations and password reset emails.
            </p>
          </div>
          <p className={cn('text-xs', colors.textMuted)}>
            Customize subject and HTML body for transactional emails. Use placeholders as documented below.
          </p>
          {templateBody}
        </div>
      </div>
      {editModal}
    </>
  );
};

export default SystemEmailTemplatesPage;
